import { MARKETING_ATTRIBUTION_COOKIE_NAME } from '@vrplatform/tracking';
import type { useSignIn } from '@clerk/react';

type ClerkErrorLike = {
  errors?: Array<{
    code?: string;
    longMessage?: string;
    message?: string;
    shortMessage?: string;
  }>;
  message?: string;
};

const asClerkErrorLike = (
  error: unknown
): ClerkErrorLike | null | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;

  return error as ClerkErrorLike;
};

const ALREADY_SIGNED_IN_CODES = new Set([
  'already_signed_in',
  'identifier_already_signed_in',
  'session_exists',
]);

// Returns the raw root-domain attribution cookie; the Clerk webhook validates it.
export const readMarketingAttributionCookie = (cookie: string) =>
  cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${MARKETING_ATTRIBUTION_COOKIE_NAME}=`))
    ?.slice(MARKETING_ATTRIBUTION_COOKIE_NAME.length + 1);

export const getClerkErrorCode = (error: unknown) =>
  asClerkErrorLike(error)?.errors?.[0]?.code;

export const getClerkErrorMessage = (
  error: unknown,
  fallbackMessage: string
) => {
  const clerkError = asClerkErrorLike(error);

  return (
    clerkError?.errors?.[0]?.longMessage ||
    clerkError?.errors?.[0]?.shortMessage ||
    clerkError?.errors?.[0]?.message ||
    clerkError?.message ||
    fallbackMessage
  );
};

export const isAlreadySignedInClerkError = (error: unknown) => {
  const code = getClerkErrorCode(error)?.toLowerCase();
  if (code && ALREADY_SIGNED_IN_CODES.has(code)) return true;

  return /already signed in/i.test(getClerkErrorMessage(error, ''));
};

export type SecondFactorSubmission = {
  code: string;
  strategy: 'totp' | 'backup_code';
};

export const verifyAndFinalizeSecondFactor = async (
  signIn: ReturnType<typeof useSignIn>['signIn'],
  { code, strategy }: SecondFactorSubmission
) => {
  const { error: secondFactorError } =
    strategy === 'totp'
      ? await signIn.mfa.verifyTOTP({ code })
      : await signIn.mfa.verifyBackupCode({ code });

  if (secondFactorError) throw secondFactorError;

  if (signIn.status === 'complete') {
    const { error: finalizeError } = await signIn.finalize();
    if (finalizeError) throw finalizeError;
  }

  return signIn.status;
};
